import React, { useRef, useState } from "react";
import { HomeOutlined, SearchOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Input, Space, Table, Alert, Switch } from "antd";
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import axios from 'axios';
import useSWR from 'swr';
import "./data_supply_slip.css";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";

interface SupplySlipDataType {
    key: React.Key;
    id: number;
    status: string;
    supply_slip_no: string;
    supply_slip_date: string;
    work_order_no: string;
    work_order_date: string;
    no_polisi?: string;
    warehouse: string;
    warehouse_name: string;
}


type DataIndex = keyof SupplySlipDataType;

const fetcher = async (url: string) => {
    const res = await axios.get(url);
    return res.data;
};

const mapStatusNumberToString = (statusNum: number): string => {
    switch (statusNum) {
        case 1: return 'Draft';
        case 7: return 'Ready';
        case 5: return 'Canceled';
        case 4: return 'Complete';
        default: return String(statusNum);
    }
};

const DataSupplySlip: React.FC = () => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [sortField, setSortField] = useState<string>('supply_slip_date');
    const [sortOrder, setSortOrder] = useState<string>('descend');
    const searchInput = useRef<InputRef>(null);
    const navigate = useNavigate();
    const { userData } = useAuth();

    const [showAllWarehouse, setShowAllWarehouse] = useState<boolean>(false);
    const hasAuthority77 = userData?.otoritas?.split(';').map((val: string) => val.trim()).includes('77');

    const { data: supplySlipData, error, isLoading } = useSWR(
        `${process.env.REACT_APP_BACKEND_URL}/api/supply-slip/all?search=${searchText}&searchColumn=${searchedColumn}&sortBy=${sortField}&sortOrder=${sortOrder}&warehouse=${showAllWarehouse ? '' : (userData?.warehouse_code || '')}`,
        fetcher
    );

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };


    const handleReset = (clearFilters: () => void, confirm?: FilterDropdownProps['confirm']) => {
        clearFilters();
        setSearchText('');
        setSearchedColumn('');
        if (confirm) {
            confirm();
        }
    };

    const handleTableChange = (pagination: any, filters: any, sorter: any) => {
        if (sorter && sorter.field) {
            setSortField(sorter.field);
            setSortOrder(sorter.order || 'descend');
        }
    };


    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<SupplySlipDataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${String(dataIndex)}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters, confirm)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) => {
            const recordValue = record[dataIndex];
            if (recordValue === undefined || recordValue === null) return false;
            return recordValue
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase());
        },
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });


    const columns: TableColumnsType<SupplySlipDataType> = [
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: '10%',
            filters: [
                { text: 'Draft', value: '1' },
                { text: 'Ready', value: '7' },
                { text: 'Canceled', value: '5' },
                { text: 'Complete', value: '4' },
            ],
            onFilter: (value, record) => String(record.status) === String(value),
            sorter: true,
            sortDirections: ['ascend', 'descend'],
            render: (status: string) => {
                const statusNum = parseInt(status, 10);
                switch (statusNum) {
                    case 1: return 'Draft';
                    case 7: return 'Ready';
                    case 5: return 'Canceled';
                    case 4: return 'Complete';
                    default: return status; 
                }
            },
        },
        {
            title: 'Supply Slip No',
            dataIndex: 'supply_slip_no',
            key: 'supply_slip_no',
            width: '15%',
            ...getColumnSearchProps('supply_slip_no'),
            sorter: true,
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: 'Supply Slip Date',
            dataIndex: 'supply_slip_date',
            key: 'supply_slip_date',
            width: '15%',
            ...getColumnSearchProps('supply_slip_date'),
            sorter: true,
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: 'Work Order No',
            dataIndex: 'work_order_no',
            key: 'work_order_no',
            width: '15%',
            ...getColumnSearchProps('work_order_no'),
            sorter: true,
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: 'Work Order Date',
            dataIndex: 'work_order_date',
            key: 'work_order_date',
            width: '15%',
            ...getColumnSearchProps('work_order_date'),
            sorter: true,
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: 'No Polisi',
            dataIndex: 'no_polisi',
            key: 'no_polisi',
            width: '10%',
            ...getColumnSearchProps('no_polisi'),
            sorter: true,
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: 'Warehouse',
            dataIndex: 'warehouse_name',
            key: 'warehouse_name',
            width: '20%',
            ...getColumnSearchProps('warehouse_name'),
            sorter: true,
            sortDirections: ['ascend', 'descend'],
        },
    ];

    return (

        <div id="i3se" style={transformStyle}>
            <div className="gjs-grid-row" id="ihw4">
                <div className="gjs-grid-column" id="i99l">
                    <Breadcrumb
                        items={[
                            {
                                title: <HomeOutlined />,
                            },
                            {
                                title: (
                                    <>
                                        <span>Data Supply Slip</span>
                                    </>
                                ),
                            },
                            {
                                href: "#/dashboard/supply_slip",
                                title: "Supply Slip",
                            },
                        ]}
                    />
                </div>
            </div>
            <div className="gjs-grid-row" id="ipm8">
                <div className="gjs-grid-column" id="ivbs">
                    {error && <Alert message="Error" description="Gagal memuat data Supply Slip." type="error" showIcon style={{ marginBottom: 16 }} />}
                    {hasAuthority77 && (
                        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Switch checked={showAllWarehouse} onChange={setShowAllWarehouse} />
                            <span style={{ fontSize: '14px' }}>Show All Warehouse</span>
                        </div>
                    )}
                    <Table<SupplySlipDataType>
                        columns={columns}
                        dataSource={supplySlipData?.map((item: any) => ({
                            ...item,
                            key: item.id,
                        })) || []}
                        loading={isLoading}
                        pagination={{ pageSize: 10 }}
                        scroll={{ x: 'max-content' }}
                        onChange={handleTableChange}
                        onRow={(record) => {
                            return {
                                onDoubleClick: () => {
                                    const statusLabel = mapStatusNumberToString(parseInt(record.status, 10));
                                    navigate(`/dashboard/supply_slip?supplySlipNo=${record.supply_slip_no}&statusLabel=${statusLabel}`);
                                },
                            };
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default DataSupplySlip;